/**
 * Monthly Sales Trend Chart Component
 *
 * Displays year-to-date sales trend with:
 * - Bar chart for monthly revenue
 * - Line chart for month-over-month growth percentage
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";
import type { MonthlyDataPoint } from "@/shared/types/sales-analytics";

interface MonthlySalesTrendChartProps {
  data?: MonthlyDataPoint[];
  metric?: 'revenue' | 'sales';
  onMetricChange?: (metric: 'revenue' | 'sales') => void;
  title?: string;
  description?: string;
}

export function MonthlySalesTrendChart({
  data,
  metric = 'revenue',
  onMetricChange,
  title = "Sales Trend This Year",
  description = "Monthly revenue and growth progression"
}: MonthlySalesTrendChartProps) {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-center py-8">No trend data available</div>
        </CardContent>
      </Card>
    );
  }

  // Dynamic Y-axis formatter
  const formatYAxis = (value: number) => {
    if (value >= 1000000) {
      return `฿${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `฿${(value / 1000).toFixed(0)}K`;
    }
    return `฿${value}`;
  };

  // Color bars based on growth (green for positive, red for negative, blue for first month)
  const getBarColor = (index: number, growthPercentage: number) => {
    if (index === 0) return "#3b82f6"; // blue for first month (no comparison)
    return growthPercentage >= 0 ? "#10b981" : "#ef4444"; // green/red based on growth
  };

  // Dynamic labels based on metric type
  const metricLabel = metric === 'sales' ? 'Sales' : 'Revenue';
  const metricDescription = metric === 'sales'
    ? 'Based on invoice creation date'
    : 'Based on payment received date';

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: {
    active?: boolean;
    payload?: ReadonlyArray<{ payload: MonthlyDataPoint }>;
  }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-sm mb-2">{data.monthName} {data.year}</p>
        <p className="text-sm text-gray-600">
          {metricLabel}: <span className="font-semibold text-blue-600">
            ฿{data.revenue.toLocaleString()}
          </span>
        </p>
        <p className="text-sm text-gray-600">
          Projects: <span className="font-semibold">{data.projectCount}</span>
        </p>
        {data.month > 1 && (
          <p className="text-sm text-gray-600">
            Growth: <span className={`font-semibold ${
              data.growthPercentage >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {data.growthPercentage >= 0 ? '+' : ''}{data.growthPercentage.toFixed(1)}%
            </span>
          </p>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </div>

          {/* Metric Toggle - Only show if onMetricChange is provided */}
          {onMetricChange && (
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <Button
                variant={metric === 'revenue' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onMetricChange('revenue')}
                className="h-8 px-3"
              >
                Revenue (Paid)
              </Button>
              <Button
                variant={metric === 'sales' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onMetricChange('sales')}
                className="h-8 px-3"
              >
                Sales (Invoiced)
              </Button>
            </div>
          )}
        </div>

        {/* Metric description - Only show if onMetricChange is provided */}
        {onMetricChange && (
          <p className="text-xs text-muted-foreground mt-2">{metricDescription}</p>
        )}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="monthName"
              tick={{ fontSize: 12 }}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(value) => `${value.toFixed(0)}%`}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              iconType="rect"
              iconSize={10}
            />

            {/* Revenue/Sales bars */}
            <Bar
              yAxisId="left"
              dataKey="revenue"
              name={metricLabel}
              radius={[4, 4, 0, 0]}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(index, entry.growthPercentage)} />
              ))}
            </Bar>

            {/* Growth percentage line (skip first month which has 0% growth) */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="growthPercentage"
              name="Growth %"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: "#f59e0b", r: 4 }}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t text-sm text-muted-foreground text-center">
          Total: ฿{data.reduce((sum, m) => sum + m.revenue, 0).toLocaleString()}
          {' • '}
          {data.length} {data.length === 1 ? 'month' : 'months'}
        </div>
      </CardContent>
    </Card>
  );
}
